/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2024 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { HTMLAttributes, useEffect, useState } from 'preact/compat';
import { Container } from '@adobe-commerce/elsie/lib';
import { Header } from '@/pdp/components';
import { events } from '@adobe-commerce/event-bus';
import { ProductModel } from '@/pdp/data/models';
import { getFetchedProductData } from '@/pdp/api';

export interface ProductHeaderProps extends HTMLAttributes<HTMLDivElement> {
  scope?: string;
  hideSku?: boolean;
}

export const ProductHeader: Container<
  ProductHeaderProps,
  ProductModel | null
> = ({ initialData = null, scope, hideSku = false, children, ...props }) => {
  const [data, setData] = useState<ProductModel | null>(initialData);

  useEffect(() => {
    const dataEvent = events.on('pdp/data', setData, { scope });

    return () => {
      dataEvent?.off();
    };
  }, [scope]);

  if (!data) return null;

  return (
    <Header
      {...props}
      title={<>{data.name}</>}
      sku={<>{data.sku}</>}
      hideSku={hideSku}
    />
  );
};

ProductHeader.getInitialData = getFetchedProductData;
